import React, { memo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LockIcon } from '@phosphor-icons/react';
import SelectDropdown from 'src/components/common/SelectDropdown';
import CurrencyConversionCard from './CurrencyConversionCard';
import type { WorkflowConstants } from '../types/WorkflowConstants';
import { CURRENCY_CODE_VALUES } from '@erp/shared-types';

/**
 * IMPORTANT: Currency handling is currently a temporary solution.
 * This component displays Company.metadata.defaultCurrency fetched from
 * the supplier, but allows user override per-upload.
 *
 * TODO: Future refactoring needed for proper multi-currency support.
 * @see packages/shared-types/src/currency.ts for more details
 */

interface WorkflowConstantsPanelProps {
  constants: WorkflowConstants | null;
  supplierName?: string;
  onConstantsChange?: (constants: WorkflowConstants) => void;
  phase: 1 | 2 | 3; // Which phase we're in
  disabled?: boolean;
  // Currency conversion props (Phase 3 only)
  onApplyCurrencyConversion?: (rate: number) => void;
  conversionApplied?: boolean;
  showWorkflowCompleted?: boolean; // Only show conversion after workflow completes
  onConversionRateChange?: (rate: number | undefined) => void;
}

// Currency options for dropdown
// NOTE: Uses shared currency codes from @erp/shared-types
const CURRENCY_OPTIONS = CURRENCY_CODE_VALUES.map((code) => ({
  code,
  label: `${code} - ${getCurrencyLabel(code)}`,
}));

function getCurrencyLabel(code: string): string {
  const labels: Record<string, string> = {
    CAD: 'Canadian Dollar',
    USD: 'US Dollar',
    EUR: 'Euro',
    GBP: 'British Pound',
  };
  return labels[code] || code;
}

const WorkflowConstantsPanel: React.FC<WorkflowConstantsPanelProps> = memo(
  ({
    constants,
    supplierName,
    onConstantsChange,
    phase,
    disabled = false,
    onApplyCurrencyConversion,
    conversionApplied,
    showWorkflowCompleted = false,
    onConversionRateChange,
  }) => {
    const isEditable = !disabled && onConstantsChange;

    const handleChange = useCallback(
      (
        key: keyof WorkflowConstants,
        value: string | Record<string, string>,
      ) => {
        if (!onConstantsChange || !constants) return;
        onConstantsChange({
          ...constants,
          [key]: value,
        });
      },
      [constants, onConstantsChange],
    );

    if (!constants) return null;

    return (
      <div className="mb-6">
        <Card>
          <CardContent className="py-4 px-5">
            {/* Horizontal layout with grid */}
            <div className="grid grid-cols-12 gap-6 items-center">
              {/* Supplier - Always locked after Phase 0 - Takes up 4 columns */}
              <div className="col-span-4">
                <Label className="text-xs text-gray-500 mb-1.5 block">
                  Supplier
                </Label>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <LockIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate">
                    {supplierName || constants.supplierId}
                  </span>
                </div>
              </div>

              {/* Currency - Editable - Takes up 3 columns */}
              <div className="col-span-3">
                <Label className="text-xs text-gray-500 mb-1.5 block">
                  Source Currency
                </Label>
                {isEditable ? (
                  <SelectDropdown
                    options={CURRENCY_OPTIONS.map((c) => ({
                      id: c.code,
                      label: c.label,
                      display: c.code,
                    }))}
                    value={
                      CURRENCY_OPTIONS.map((c) => ({
                        id: c.code,
                        label: c.label,
                        display: c.code,
                      })).find((o) => o.display === constants.currency) || null
                    }
                    onChange={(selected) =>
                      handleChange('currency', selected?.display || '')
                    }
                    size="small"
                    disabled={disabled}
                    required
                    error={!constants.currency}
                    helperText={!constants.currency ? 'Required' : undefined}
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">
                    {constants.currency}
                  </p>
                )}
              </div>

              {/* Date Range - Editable - Takes up 5 columns */}
              <div className="col-span-5">
                <Label className="text-xs text-gray-500 mb-1.5 block">
                  Date Range <span className="text-gray-400">(Optional)</span>
                </Label>
                {isEditable ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      placeholder="2025-01-01"
                      value={constants.dateRange?.startDate || ''}
                      onChange={(e) =>
                        handleChange('dateRange', {
                          startDate: e.target.value,
                          endDate: constants.dateRange?.endDate || '',
                        })
                      }
                      disabled={disabled}
                      className={`flex-1 ${
                        Boolean(constants.dateRange?.startDate) &&
                        !/^\d{4}-\d{2}-\d{2}$/.test(
                          constants.dateRange?.startDate || '',
                        )
                          ? 'border-red-500'
                          : ''
                      }`}
                    />
                    <span className="text-gray-400 text-sm">→</span>
                    <Input
                      type="text"
                      placeholder="2025-12-31"
                      value={constants.dateRange?.endDate || ''}
                      onChange={(e) =>
                        handleChange('dateRange', {
                          startDate: constants.dateRange?.startDate || '',
                          endDate: e.target.value,
                        })
                      }
                      disabled={disabled}
                      className={`flex-1 ${
                        Boolean(constants.dateRange?.endDate) &&
                        !/^\d{4}-\d{2}-\d{2}$/.test(
                          constants.dateRange?.endDate || '',
                        )
                          ? 'border-red-500'
                          : ''
                      }`}
                    />
                  </div>
                ) : constants.dateRange?.startDate &&
                  constants.dateRange?.endDate ? (
                  <p className="text-sm font-medium text-gray-900">
                    {constants.dateRange.startDate} →{' '}
                    {constants.dateRange.endDate}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 italic">Not set</p>
                )}
                {isEditable &&
                ((Boolean(constants.dateRange?.startDate) &&
                  !/^\d{4}-\d{2}-\d{2}$/.test(
                    constants.dateRange?.startDate || '',
                  )) ||
                  (Boolean(constants.dateRange?.endDate) &&
                    !/^\d{4}-\d{2}-\d{2}$/.test(
                      constants.dateRange?.endDate || '',
                    ))) ? (
                  <p className="text-xs text-red-500 mt-1">
                    Must be YYYY-MM-DD format
                  </p>
                ) : null}
              </div>
            </div>

            {/* Helper text below for Phase 3 */}
            {isEditable && phase === 3 && (
              <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">
                💡 Changes to currency or date range will require re-running the
                workflow
              </p>
            )}
          </CardContent>
        </Card>

        {/* Currency Conversion Card - Phase 3 only, after workflow completes */}
        {phase === 3 &&
          showWorkflowCompleted &&
          onApplyCurrencyConversion &&
          constants.currency && (
            <div className="mt-4">
              <CurrencyConversionCard
                sourceCurrency={constants.currency}
                targetCurrency={constants.targetCurrency || 'CAD'}
                currentRate={constants.conversionRate}
                onApplyConversion={onApplyCurrencyConversion}
                disabled={disabled}
                hasBeenApplied={conversionApplied}
                onRateChange={onConversionRateChange}
              />
            </div>
          )}
      </div>
    );
  },
);

WorkflowConstantsPanel.displayName = 'WorkflowConstantsPanel';

export default WorkflowConstantsPanel;
